import React from "react";
import {
  CONDICIONES_BO,
  COMO_EMPEZAMOS_BO,
  CONTACTO_BO,
  EN_TODOS_LOS_PLANES,
  EXTRAS_BO,
  IVA_LEYENDA,
  LEYENDA_BO,
  MATRIZ_BO,
  NOTA_MATRIZ_BO,
  PIE_MATRIZ_BO,
  PLANES_BO,
  PLAN_DEFAULT,
  PLAN_KEYS,
  VALIDEZ_DIAS_DEFAULT,
  fechaCortaBO,
  fechaLargaBO,
  fechaVencimientoBO,
  fmtU,
  rotuloPrecioUnidadBO,
} from "../../utils/planesBackoffice";
import logoBlanco from "../../assets/habitia-logo-white.png";
import isotipoHabitia from "../../assets/isotipo.png";
import "./presupuestoTemplate.css";
import "./cotizacionBackofficeTemplate.css";

// Propuesta Back office para administradoras: documento A4 de 5 hojas que se
// captura con html2canvas (utils/presupuestoPdf.js → generarPresupuestoPdf,
// una captura por cada section.page).
//
// data = {
//   nro, fecha, validezHasta,                    // Date | ISO
//   administradora: { nombre, contactoNombre? },
//   cotizacion,                                   // salida de cotizarBackoffice
//   preparadoPor: { nombre, email },
//   mostrarListaTachada,                          // tacha la lista: solo con totalFinal <
//                                                 // totalCalculado y el precio de lista
// }
//
// Todos los montos van "+ IVA": nunca se muestran montos con IVA ni el IVA.
// Cada hoja mide 297 mm fijos (overflow hidden): lo que no entra se corta, así
// que el banco de pruebas visual (html2canvas en Chrome) controla que nada se salga.

const PAGINAS = ["portada", "plan", "economica", "matriz", "cierre"];
const MAX_NOMBRE_PIE = 42;
const CLASE_VALOR = { vos: "vos", habitia: "hab", incluido: "inc", extra: "ext", no: "no" };
const ETIQUETA_VALOR = LEYENDA_BO.reduce((acc, l) => ({ ...acc, [l.valor]: l.etiqueta }), {});

const pad2 = (n) => String(n).padStart(2, "0");

function numero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function entero(v) {
  return Math.max(0, Math.floor(numero(v)));
}

// 1234 → "1.234" (separador de miles uruguayo, sin depender del locale).
function miles(n) {
  return String(entero(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function cantidad(n, uno, varios) {
  const v = entero(n);
  return `${miles(v)} ${v === 1 ? uno : varios}`;
}

// Recorta en el último espacio antes del máximo (html2canvas no dibuja
// text-overflow: ellipsis, así que el corte se hace en el texto).
function truncar(texto, max) {
  const s = String(texto || "").trim();
  if (s.length <= max) return s;
  const corte = s.slice(0, max - 1);
  const espacio = corte.lastIndexOf(" ");
  const base = espacio > max * 0.6 ? corte.slice(0, espacio) : corte;
  return `${base.replace(/[\s,.;:·-]+$/, "")}…`;
}

// "Back office" y "Full Back office" no se parten entre renglones (espacio duro).
function sinCorte(texto) {
  return String(texto || "")
    .replace(/\bFull Back office\b/g, "Full\u00a0Back\u00a0office")
    .replace(/\b([Bb]ack) office\b/g, "$1\u00a0office");
}

// Parte la frase del plan para resaltar su segunda mitad con <em>:
// después de "; ", si no después de la última ", ", si no desde el primer " y ".
function partirFrase(frase) {
  const s = String(frase || "");
  let i = s.indexOf("; ");
  if (i >= 0) return [s.slice(0, i + 2), s.slice(i + 2)];
  i = s.lastIndexOf(", ");
  if (i >= 0) return [s.slice(0, i + 2), s.slice(i + 2)];
  i = s.indexOf(" y ");
  if (i >= 0) return [s.slice(0, i + 3), s.slice(i + 3)];
  return [s, ""];
}

// Primera parte de la nota de la matriz (hasta el paréntesis) en tinta más oscura, como el deck.
function partirNota(nota) {
  const s = String(nota || "");
  const i = s.indexOf(" (");
  return i > 0 ? [s.slice(0, i), s.slice(i)] : [s, ""];
}

function Lvl({ nivel }) {
  return (
    <span className={`bo-lvl l${nivel}`} aria-hidden="true">
      <i />
      <i />
      <i />
    </span>
  );
}

function Pill({ valor }) {
  return <span className={`bo-who ${CLASE_VALOR[valor] || "no"}`}>{ETIQUETA_VALOR[valor] || "—"}</span>;
}

function Pie({ n, total, etiqueta }) {
  return (
    <div className="pfooter">
      <div className="br">
        <img src={isotipoHabitia} alt="" width="28" height="28" />
        {etiqueta}
      </div>
      <div className="pn">
        {pad2(n)} / {pad2(total)}
      </div>
    </div>
  );
}

const svgProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "1.6",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
};

// Íconos de las condiciones, en el orden de CONDICIONES_BO.
const ICONOS_CONDICION = [
  // Por unidad, sin mínimos
  <svg {...svgProps} key="unidad">
    <rect x="5" y="3" width="14" height="18" rx="1.5" />
    <path d="M9 7h.01M15 7h.01M9 11h.01M15 11h.01M9 15h.01M15 15h.01M10.5 21v-3h3v3" />
  </svg>,
  // Sin permanencia
  <svg {...svgProps} key="permanencia">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 7.6-1.8" />
  </svg>,
  // Migración sin costo
  <svg {...svgProps} key="migracion">
    <path d="M4 8h14M14 4l4 4-4 4M20 16H6M10 12l-4 4 4 4" />
  </svg>,
  // Nada presencial
  <svg {...svgProps} key="presencial">
    <rect x="3" y="4" width="18" height="12" rx="2" />
    <path d="M8 20h8M12 16v4" />
  </svg>,
];

const CotizacionBackofficeTemplate = React.forwardRef(function CotizacionBackofficeTemplate({ data }, ref) {
  const d = data || {};
  const c = d.cotizacion || {};
  const planKey = PLAN_KEYS.includes(c.plan) ? c.plan : PLAN_DEFAULT;
  const plan = PLANES_BO[planKey];
  const planNombre = sinCorte(c.planNombre || plan.nombre);

  const adm = d.administradora || {};
  const nombre = String(adm.nombre || "").trim();
  const contacto = String(adm.contactoNombre || "").trim();

  const fecha = d.fecha || new Date();
  const validezHasta = d.validezHasta || fechaVencimientoBO(fecha, VALIDEZ_DIAS_DEFAULT);
  const nro = String(d.nro || "");

  const unidades = entero(c.unidades);
  const edificios = entero(c.edificios);
  const extras = Array.isArray(c.extras) ? c.extras.filter((x) => x && numero(x.monto) > 0) : [];
  const precioLista = numero(c.precioLista) || plan.precioDesde;
  const precioUnidad = numero(c.precioUnidad);
  const precioUnidadFinal = numero(c.precioUnidadFinal);
  const totalCalculado = numero(c.totalCalculado);
  const totalFinal = numero(c.totalFinal);

  // Se tacha la LISTA, y solo si el total final quedó por debajo del calculado.
  // Con el precio por unidad editado no se tacha nada: lo tachado no sería la
  // lista (chocaría con el "desde" de las hojas 2 y 4) y, si se tachara la
  // lista junto al total calculado con otro precio, las dos cifras no cerrarían.
  const precioEsLista = Math.abs(precioUnidad - precioLista) < 0.005;
  const tachar = !!d.mostrarListaTachada && precioEsLista && totalFinal < totalCalculado - 0.005;
  const tacharUnidad = tachar && precioLista > precioUnidadFinal + 0.005;
  const totalTxt = fmtU(totalFinal);
  // "Precio promedio por unidad" si el precio no reproduce el total al centésimo.
  const rotuloUnidad = rotuloPrecioUnidadBO(c);

  const unidadesTxt = cantidad(unidades, "unidad", "unidades");
  const edificiosTxt = edificios > 0 ? cantidad(edificios, "edificio", "edificios") : "";
  const carteraLinea = edificiosTxt ? `${edificiosTxt} · ${unidadesTxt}` : unidadesTxt;
  const carteraLead = edificiosTxt ? `${edificiosTxt} y ${unidadesTxt}` : unidadesTxt;

  const [fraseA, fraseB] = partirFrase(plan.frase).map(sinCorte);
  const [notaA, notaB] = partirNota(NOTA_MATRIZ_BO);
  const etiquetaPie = nombre
    ? `habitia · Propuesta Back office para ${truncar(nombre, MAX_NOMBRE_PIE)}`
    : "habitia · Propuesta Back office";
  const contactoNombre = (d.preparadoPor && d.preparadoPor.nombre) || CONTACTO_BO.nombre;
  const contactoEmail = (d.preparadoPor && d.preparadoPor.email) || CONTACTO_BO.email;

  const totalPaginas = PAGINAS.length;
  const nPag = (clave) => PAGINAS.indexOf(clave) + 1;

  return (
    <div ref={ref} className="habitia-presupuesto habitia-bo">
      {/* ============== 1 · PORTADA ============== */}
      <section className="page cover" data-pagina="portada">
        <div className="cover-top">
          <img src={logoBlanco} alt="habitia" width="144" height="64" />
          <div className="meta">
            <b>Propuesta&nbsp;Nº&nbsp;{nro}</b>{" "}
            <span>Montevideo · {fechaCortaBO(fecha)}</span>
          </div>
        </div>

        <div className="h-stage" aria-hidden="true">
          <div className="h-mark">
            <div className="bar-l" />
            <div className="bar-c" />
            <div className="bar-r" />
          </div>
        </div>

        <div className="cover-copy">
          <span className="eyb">{nombre ? `Propuesta para ${nombre}` : "Propuesta Habitia"}</span>
          <h1>
            Tu administración <br />
            en <em>piloto automático.</em>
          </h1>
          <p className="lead">
            Plan {planNombre} para tu cartera de {carteraLead}.
          </p>
        </div>

        <div className="cover-bottom">
          <div>
            <div className="lbl">Preparada para</div>
            <div className="val">{nombre || "—"}</div>
            {contacto && <div className="sub">A la atención de {contacto}</div>}
          </div>
          <div>
            <div className="lbl">Plan</div>
            <div className="val">{planNombre}</div>
          </div>
          <div>
            <div className="lbl">Cartera</div>
            <div className="val">
              {edificiosTxt && (
                <>
                  {edificiosTxt}
                  <br />
                </>
              )}
              {unidadesTxt}
            </div>
          </div>
          <div>
            <div className="lbl">Válida hasta</div>
            <div className="val">{fechaCortaBO(validezHasta)}</div>
          </div>
        </div>
      </section>

      {/* ============== 2 · TU PLAN ============== */}
      <section className="page" data-pagina="plan">
        <div className="page-pad">
          <span className="sec-tag">01 · Tu plan</span>
          <h2 className="big">
            {fraseA}
            {fraseB && <em>{fraseB}</em>}
          </h2>

          <div className="bo-plan" style={{ "--plan": plan.color }}>
            <div className="bo-plan-main">
              <div className="pk">
                <Lvl nivel={plan.nivel} />
                {plan.kicker}
              </div>
              <h3 className="nm">{sinCorte(plan.nombre)}</h3>
              <p className="rs">{plan.resumen}</p>
              <div className="pr bo-iva">
                <span className="from">desde</span>{" "}
                <span className="v">{fmtU(plan.precioDesde)}</span>{" "}
                <span className="u">por unidad · mes · {IVA_LEYENDA}</span>
              </div>
            </div>
            <div className="bo-roles">
              {plan.roles.map((r) => (
                <div className="rl" key={r.quien}>
                  <span className="rk">{r.quien}</span>{" "}
                  <span>{r.que}</span>
                </div>
              ))}
            </div>
          </div>

          <h3 className="bo-h3 bo-incl-h">{sinCorte(plan.incluyeTitulo)}</h3>
          <ul className="bo-incl">
            {plan.incluye.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          {plan.incluyeAnterior && <p className="bo-anterior">{sinCorte(plan.incluyeAnterior)}</p>}

          {plan.sigueConVos && (
            <div className="bo-keep">
              <span className="kk">{plan.sigueConVosTitulo}</span> {plan.sigueConVos}
            </div>
          )}

          <div className="bo-hoycon">
            <div className="b hoy">
              <span className="bk">Hoy</span> {plan.hoy}
            </div>
            <div className="arw" aria-hidden="true">
              <svg {...svgProps} strokeWidth="1.8">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </div>
            <div className="b con">
              <span className="bk">Con Habitia</span> {plan.conHabitia}
            </div>
          </div>

          <div className="bo-strip">
            <span className="sk">En todos los planes</span>{" "}
            <span>{EN_TODOS_LOS_PLANES}</span>
          </div>
        </div>
        <Pie n={nPag("plan")} total={totalPaginas} etiqueta={etiquetaPie} />
      </section>

      {/* ============== 3 · PROPUESTA ECONÓMICA ============== */}
      <section className="page" data-pagina="economica">
        <div className="page-pad">
          <span className="sec-tag">02 · Propuesta económica</span>
          <h2 className="big bo-h2-ancho">
            Tu inversión mensual, <em>por unidad.</em>
          </h2>
          <p className="lead">Montos en pesos uruguayos, por mes, {IVA_LEYENDA}.</p>

          <div className="bo-precio">
            <div className="hd">
              <span>Inversión mensual</span>{" "}
              {nro && <span className="nro">Propuesta Nº {nro}</span>}
            </div>
            <div className="rows">
              <div className="bo-row">
                <span className="k">Plan</span>
                <span className="v">
                  <Lvl nivel={plan.nivel} />
                  {planNombre}
                </span>
              </div>
              <div className="bo-row">
                <span className="k">Cartera</span>
                <span className="v">{carteraLinea}</span>
              </div>
              <div className="bo-row bo-row-unidad">
                <span className="k">{rotuloUnidad}</span>
                <span className="v bo-iva">
                  {tacharUnidad && (
                    <>
                      <span className="tach bo-lista">{fmtU(precioLista)}</span>{" "}
                    </>
                  )}
                  {fmtU(precioUnidadFinal)} <span className="per">por mes {IVA_LEYENDA}</span>
                </span>
              </div>
              {extras.map((x) => (
                <div className="bo-row bo-row-extra" key={x.key} data-extra={x.key}>
                  <span className="k">
                    {x.label}
                    {EXTRAS_BO[x.key] && EXTRAS_BO[x.key].nota && <small> {EXTRAS_BO[x.key].nota}</small>}
                  </span>
                  <span className="v bo-iva">
                    {fmtU(x.monto)} <span className="per">por mes {IVA_LEYENDA}</span>
                  </span>
                </div>
              ))}
            </div>
            <div className={`bo-total${totalTxt.length > 12 ? " is-largo" : ""}`}>
              <div className="k">
                Total mensual{" "}
                <small>
                  Plan {planNombre} · {unidadesTxt}
                </small>
              </div>
              <div className="tot bo-iva">
                {tachar && <span className="tach bo-lista">{fmtU(totalCalculado)}</span>}
                <span className="big">{totalTxt}</span> <span className="iva">{IVA_LEYENDA}</span>{" "}
                <span className="pm">por mes</span>
              </div>
            </div>
          </div>
          <p className="bo-validez">
            Válida hasta el <span>{fechaLargaBO(validezHasta)}</span>.
          </p>

          <div className="bo-bloque">
            <h3 className="bo-h3">Condiciones</h3>
            <div className="chips">
              {CONDICIONES_BO.map((cond, i) => (
                <div className="chip" key={cond.titulo}>
                  <div className="ic">{ICONOS_CONDICION[i]}</div>
                  <div>
                    <h4>{cond.titulo}</h4>
                    <p>{cond.texto}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bo-bloque">
            <h3 className="bo-h3">Cómo empezamos</h3>
            <ol className="bo-pasos">
              {COMO_EMPEZAMOS_BO.map((p, i) => (
                <li className={`bo-paso${i === COMO_EMPEZAMOS_BO.length - 1 ? " fill" : ""}`} key={p.n}>
                  <div className="n">{p.n}</div>
                  <div className="k">{p.titulo}</div>
                  <div className="t">{p.sub}</div>
                  <p>{p.texto}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
        <Pie n={nPag("economica")} total={totalPaginas} etiqueta={etiquetaPie} />
      </section>

      {/* ============== 4 · QUIÉN HACE CADA COSA ============== */}
      <section className="page" data-pagina="matriz">
        <div className="page-pad">
          <span className="sec-tag">03 · Quién hace cada cosa</span>
          <h2 className="big bo-h2-ancho">
            Quién hace cada cosa, <em>plan por plan.</em>
          </h2>

          <div className="bo-leyenda">
            {LEYENDA_BO.map((l) => (
              <span className="it" key={l.valor}>
                <Pill valor={l.valor} /> {l.significado}
              </span>
            ))}
          </div>

          <div className={`bo-mx sel-${planKey}`}>
            <div className="bo-mx-sel" aria-hidden="true" />
            <div className="bo-mx-head">
              <div />
              <div />
              {PLAN_KEYS.map((k) => {
                const p = PLANES_BO[k];
                const sel = k === planKey;
                return (
                  <div className={`bo-mx-hc${sel ? " is-sel" : ""}`} data-plan={k} key={k}>
                    {sel && <span className="bo-tuplan">Tu plan</span>}
                    <Lvl nivel={p.nivel} />
                    <span className="pn">{sinCorte(p.nombre)}</span>
                    <span className="bo-iva">
                      <span className="pp">desde {fmtU(p.precioDesde)}</span>{" "}
                      <span className="pu">unidad · mes · {IVA_LEYENDA}</span>
                    </span>
                  </div>
                );
              })}
            </div>

            {MATRIZ_BO.map((grupo) => (
              <div className="bo-mx-grp" key={grupo.key} data-grupo={grupo.key}>
                <div className="bo-mx-g">{grupo.label}</div>
                <div>
                  {grupo.filas.map((fila) => (
                    <div className="bo-mx-row" key={fila.key} data-fila={fila.key}>
                      <div className="bo-mx-f">{fila.label}</div>
                      {PLAN_KEYS.map((k) => {
                        const sel = k === planKey;
                        const enPropuesta =
                          sel && fila[k] === "extra" && extras.some((e) => e.key === fila.key);
                        return (
                          <div className={`bo-mx-c${sel ? " is-sel" : ""}`} data-plan={k} key={k}>
                            {enPropuesta ? (
                              <>
                                <Pill valor="incluido" /> <span className="bo-enprop">en esta propuesta</span>
                              </>
                            ) : (
                              <Pill valor={fila[k]} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="bo-mx-nota">
            <span className="fuerte">{notaA}</span>
            {notaB}
          </p>
          <p className="bo-mx-nota">
            Los precios de cada columna son de referencia, por unidad, por mes, {IVA_LEYENDA}; el de esta
            propuesta figura en la propuesta económica.
          </p>
          <p className="bo-mx-pie">{PIE_MATRIZ_BO}</p>
        </div>
        <Pie n={nPag("matriz")} total={totalPaginas} etiqueta={etiquetaPie} />
      </section>

      {/* ============== 5 · CIERRE ============== */}
      <section className="page closing" data-pagina="cierre">
        <div className="top-iso">
          <img src={logoBlanco} alt="habitia" width="95" height="42" />
        </div>
        <h2>
          Tu administración <br />
          en <em>piloto automático.</em>
        </h2>
        <p className="lead">
          Agendemos una reunión con tu cartera: vemos tu operación de hoy y arrancamos con un piloto de 2 o 3
          edificios.
        </p>

        <div className="grid">
          <div>
            <div className="lbl">Contacto</div>
            <div className="val">{contactoNombre}</div>
          </div>
          <div>
            <div className="lbl">Correo</div>
            <div className="val">{contactoEmail}</div>
          </div>
          <div>
            <div className="lbl">WhatsApp</div>
            <div className="val">{CONTACTO_BO.whatsapp}</div>
          </div>
          <div>
            <div className="lbl">Web</div>
            <div className="val">{CONTACTO_BO.web}</div>
          </div>
        </div>
        <p className="bo-legal">
          {CONTACTO_BO.razonSocial} · RUT {CONTACTO_BO.rut} · {CONTACTO_BO.ciudad}
        </p>
        <div className="pfooter-w">
          <div>{etiquetaPie}</div>
          <div>
            {pad2(nPag("cierre"))} / {pad2(totalPaginas)}
          </div>
        </div>
      </section>
    </div>
  );
});

export default CotizacionBackofficeTemplate;
